import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { focusManager, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { roteador } from './roteador';
import './estilos.css';
import { ligarTema } from './estado/tema';
import { garantirSessao } from './api';
// O cache mora em `estado/cache.ts` desde 2026-09-21: o store precisa
// esvaziá-lo ao sair e ao entrar, e daqui ele não teria como.
import { clienteQuery } from './estado/cache';

/**
 * Ao voltar para a tela, renovar a sessão **antes** de reconsultar.
 *
 * A ordem importa e não se consegue por ordem de registro de ouvinte: o de
 * dentro do React Query dispara na sequência do nosso, e a renovação é
 * assíncrona — quando ela termina, as consultas já saíram com o token morto.
 * Aqui o próprio React Query cede o gatilho: ele entrega a função que avisa
 * "houve foco", e nós só a chamamos depois de a sessão estar boa.
 *
 * Não é mais o conserto do login (esse é a deduplicação da renovação e a
 * janela de tolerância do servidor, que continuam valendo). É evitar que o
 * caminho normal de acordar o aparelho passe por uma rajada de erro 401 —
 * gastar uma ida ao servidor para descobrir o que já se sabia.
 *
 * `finally`, e não `then`: renovação que falha ainda tem de liberar o foco,
 * senão uma queda de rede deixaria o painel congelado no que ele já tinha,
 * sem nunca mais tentar consultar nada.
 */
focusManager.setEventListener((aoFocar) => {
  const aoMudar = (): void => {
    if (document.visibilityState !== 'visible') return;
    void garantirSessao().finally(() => aoFocar());
  };
  window.addEventListener('visibilitychange', aoMudar, false);
  return () => window.removeEventListener('visibilitychange', aoMudar);
});

// Antes de desenhar: a tela não pode piscar escura antes de ficar clara.
ligarTema();

const raiz = document.getElementById('raiz');
if (!raiz) throw new Error('elemento #raiz nao encontrado');

createRoot(raiz).render(
  <StrictMode>
    <QueryClientProvider client={clienteQuery}>
      <RouterProvider router={roteador} />
    </QueryClientProvider>
  </StrictMode>,
);
